import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
    Box,
    Typography,
    Grid,
    useTheme,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import ContentArea from '../../components/contentArea';
import Button from '../../components/button';
import InputField from '../../components/formItem/inputField';
import { InputFieldComponent } from '../../components/formItem';
import Dropdown from '../../components/dropdown';
import CommonCard from '../../components/card/CommonCard';
import { contentAPI } from '../../actions/imsServer';
import Popup from '../../components/popup';
import { CONTENT_TYPES, LOCALE } from '../../constants';

const ContentsEdit = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();

    const contentData = location.state?.content;

    const [formData, setFormData] = useState({
        index: '',
        type: '',
        ko_name: '',
        ja_name: '',
        de_name: '',
        en_name: '',
        file: null, // 파일 객체
        icon: null, // 기존 아이콘 URL (미리보기용)
        endurance: 0,
        strength: 0,
        balance: 0,
        flexibility: 0,
        reaction: 0,
        cooperation: 0,
        info: '',
    });

    const [isSaving, setIsSaving] = useState(false);

    // 팝업 상태
    const [popup, setPopup] = useState({
        open: false,
        message: '',
        subMessage: '',
        onConfirm: null
    });

    // 삭제 확인 팝업 상태
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

    // 팝업 표시 함수
    const showPopup = (message, subMessage = '', onConfirm = null) => {
        setPopup({
            open: true,
            message,
            subMessage,
            onConfirm: onConfirm || (() => setPopup(prev => ({ ...prev, open: false })))
        });
    };

    useEffect(() => {
        if (contentData) {
            setFormData(prev => ({
                ...prev,
                index: contentData.index || '',
                type: contentData.type !== undefined ? contentData.type : '',
                ko_name: contentData.ko_name ?? contentData.name ?? '',
                ja_name: contentData.ja_name ?? '',
                de_name: contentData.de_name ?? '',
                en_name: contentData.en_name ?? '',
                icon: contentData.icon || null,
                endurance: contentData.endurance !== undefined ? contentData.endurance : 0,
                strength: contentData.strength !== undefined ? contentData.strength : 0,
                balance: contentData.balance !== undefined ? contentData.balance : 0,
                flexibility: contentData.flexibility !== undefined ? contentData.flexibility : 0,
                reaction: contentData.reaction !== undefined ? contentData.reaction : 0,
                cooperation: contentData.cooperation !== undefined ? contentData.cooperation : 0,
                info: contentData.info || '',
            }));
        }
    }, [contentData]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleNumberChange = (field, value) => {
        // 숫자만 입력 가능하도록 처리
        if (value === '') {
            setFormData(prev => ({ ...prev, [field]: 0 }));
            return;
        }
        // 숫자만 허용 (소수점 포함)
        const numericValue = value.replace(/[^0-9.]/g, '');
        // 중복 소수점 제거
        const parts = numericValue.split('.');
        const cleanedValue = parts.length > 2
            ? parts[0] + '.' + parts.slice(1).join('')
            : numericValue;

        if (cleanedValue === '' || !isNaN(Number(cleanedValue))) {
            const numValue = cleanedValue === '' ? 0 : Number(cleanedValue);
            setFormData(prev => ({ ...prev, [field]: numValue }));
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setFormData(prev => ({ ...prev, file: file }));
        }
    };

    // 4개 언어 이름 중 하나라도 비어 있지 않은지 검사
    const hasAnyName = (data) =>
        [data.ko_name, data.ja_name, data.de_name, data.en_name].some(
            (v) => v != null && String(v).trim() !== ''
        );

    // 유효성 검사 함수
    const isFormValid = () => {
        if (!formData.index || !hasAnyName(formData) || formData.type === '') {
            return false;
        }
        return true;
    };

    const handleCancel = () => {
        navigate('/contents/list');
    };

    const handleDeleteClick = () => {
        if (!contentData || !contentData.index) {
            showPopup(t('no_content_to_delete'));
            return;
        }
        setDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        setDeleteConfirmOpen(false);
        setIsSaving(true);
        try {
            await contentAPI.updateContent({
                index: contentData.index,
                state: 1,
            });
            showPopup(t('delete_completed'), '', () => {
                setPopup(prev => ({ ...prev, open: false }));
                navigate('/contents/list');
            });
        } catch (e) {
            console.error('콘텐츠 삭제 실패:', e);
            showPopup(t('content_delete_failed'), t('please_try_again'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleSave = async () => {
        // 필수 필드 검증
        if (!formData.index || !hasAnyName(formData) || formData.type === '') {
            showPopup(t('please_enter_the_required_fields'), t('index_category_and_name_are_required'));
            return;
        }

        setIsSaving(true);
        try {
            const contentDataToSave = {
                ...formData,
                // 숫자 필드가 빈 문자열이면 0으로 변환
                endurance: formData.endurance === '' ? 0 : Number(formData.endurance),
                strength: formData.strength === '' ? 0 : Number(formData.strength),
                balance: formData.balance === '' ? 0 : Number(formData.balance),
                flexibility: formData.flexibility === '' ? 0 : Number(formData.flexibility),
                reaction: formData.reaction === '' ? 0 : Number(formData.reaction),
                cooperation: formData.cooperation === '' ? 0 : Number(formData.cooperation),
                // file 필드는 File 객체 그대로 전달 (multipart request용)
                file: formData.file,
            };

            if (contentData && contentData.index) {
                // 수정
                await contentAPI.updateContent(contentDataToSave);
                showPopup(t('content_has_been_updated'), '', () => {
                    setPopup(prev => ({ ...prev, open: false }));
                    navigate('/contents/list');
                });
            } else {
                // 등록
                try {
                    await contentAPI.registerContent(contentDataToSave);
                    showPopup(t('content_has_been_registered'), '', () => {
                        setPopup(prev => ({ ...prev, open: false }));
                        navigate('/contents/list');
                    });
                } catch (registerError) {
                    // 409 Conflict 에러이고 "already_exists" 메시지인 경우
                    if (registerError.response?.status === 409 &&
                        registerError.response?.data?.message === 'already_exists') {
                        showPopup(t('index_already_exists_please_enter_a_different_index'));
                        return;
                    }
                    // 다른 에러는 상위 catch로 전달
                    throw registerError;
                }
            }
        } catch (e) {
            console.error('콘텐츠 저장 실패:', e);
            // 409 에러는 이미 처리했으므로 다른 에러만 표시
            if (e.response?.status !== 409) {
                showPopup(t('content_save_failed'), t('please_try_again'));
            }
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <ContentArea padding='20px 10px 10px 10px'>
            <Box sx={{ px: 3, pb: 4, width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Grid container spacing={4} sx={{ flex: 1 }}>
                    {/* 왼쪽 컬럼 */}
                    <Grid item xs={12} md={6} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CommonCard sx={{ alignItems: 'stretch', gap: 3 }}>
                            <InputFieldComponent title={`${t('index')} *`}>
                                <InputField
                                    placeholder={t('input')}
                                    value={formData.index}
                                    onChange={(e) => handleChange('index', e.target.value)}
                                    disabled={!!contentData}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={`${t('category')} *`}>
                                <Dropdown
                                    options={CONTENT_TYPES}
                                    value={formData.type}
                                    onChange={(option) => handleChange('type', option.value)}
                                    placeholder={t('please_select')}
                                />
                            </InputFieldComponent>

                            {LOCALE.map((loc) => (
                                <InputFieldComponent key={loc.code} title={`${t(loc.name)} ${t('name')} *`}>
                                    <InputField
                                        placeholder={t('input')}
                                        value={formData[`${loc.code}_name`] ?? ''}
                                        onChange={(e) => handleChange(`${loc.code}_name`, e.target.value)}
                                    />
                                </InputFieldComponent>
                            ))}

                            <InputFieldComponent title={t('icon')}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        style={{ display: 'none' }}
                                        id="icon-file-input"
                                    />
                                    <label htmlFor="icon-file-input">
                                        <Button
                                            component="span"
                                            variant="normal"
                                            size="small"
                                            sx={{ minWidth: '120px' }}
                                        >
                                            {t('file')} {t('select')}
                                        </Button>
                                    </label>
                                    {formData.file && (
                                        <Typography sx={{ fontSize: '14px', color: theme.palette.figma?.white?.[80] }}>
                                            {formData.file.name}
                                        </Typography>
                                    )}
                                    {!formData.file && formData.icon && (
                                        <Typography sx={{ fontSize: '14px', color: theme.palette.figma?.white?.[80] }}>
                                            {t('existing')}: {formData.icon}
                                        </Typography>
                                    )}
                                </Box>
                            </InputFieldComponent>

                            <InputFieldComponent title={t('note')}>
                                <InputField
                                    placeholder={t('input')}
                                    value={formData.info}
                                    onChange={(e) => handleChange('info', e.target.value)}
                                />
                            </InputFieldComponent>
                        </CommonCard>
                    </Grid>

                    {/* 오른쪽 컬럼 */}
                    <Grid item xs={12} md={6} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CommonCard sx={{ alignItems: 'stretch', gap: 3 }}>
                            <InputFieldComponent title={t('endurance')}>
                                <InputField
                                    type="number"
                                    placeholder={t('input')}
                                    value={formData.endurance === 0 ? '0' : (formData.endurance || '0')}
                                    onChange={(e) => handleNumberChange('endurance', e.target.value)}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('muscular_strength')}>
                                <InputField
                                    type="number"
                                    placeholder={t('input')}
                                    value={formData.strength === 0 ? '0' : (formData.strength || '0')}
                                    onChange={(e) => handleNumberChange('strength', e.target.value)}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('balance')}>
                                <InputField
                                    type="number"
                                    placeholder={t('input')}
                                    value={formData.balance === 0 ? '0' : (formData.balance || '0')}
                                    onChange={(e) => handleNumberChange('balance', e.target.value)}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('flexibility')}>
                                <InputField
                                    type="number"
                                    placeholder={t('input')}
                                    value={formData.flexibility === 0 ? '0' : (formData.flexibility || '0')}
                                    onChange={(e) => handleNumberChange('flexibility', e.target.value)}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('agility')}>
                                <InputField
                                    type="number"
                                    placeholder={t('input')}
                                    value={formData.reaction === 0 ? '0' : (formData.reaction || '0')}
                                    onChange={(e) => handleNumberChange('reaction', e.target.value)}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('cooperation')}>
                                <InputField
                                    type="number"
                                    placeholder={t('input')}
                                    value={formData.cooperation === 0 ? '0' : (formData.cooperation || '0')}
                                    onChange={(e) => handleNumberChange('cooperation', e.target.value)}
                                />
                            </InputFieldComponent>
                        </CommonCard>
                    </Grid>
                </Grid>

                {/* 하단 버튼 */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 2,
                    px: 2.5,
                    pb: 3,
                    alignSelf: 'stretch',
                    marginTop: 'auto'
                }}>
                    <Box>
                        {contentData && contentData.index && (
                            <Button
                                variant="gray"
                                onClick={handleDeleteClick}
                                disabled={isSaving}
                                sx={{ minWidth: '120px' }}
                            >
                                {t('delete')}
                            </Button>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button variant="gray" onClick={handleCancel} sx={{ minWidth: '120px' }}>{t('cancel')}</Button>
                        <Button onClick={handleSave} disabled={isSaving || !isFormValid()} sx={{ minWidth: '120px' }}>
                            {t('save')}
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* 삭제 확인 팝업 */}
            <Popup
                open={deleteConfirmOpen}
                message={t('are_you_sure_you_want_to_delete')}
                onCancel={() => setDeleteConfirmOpen(false)}
                onConfirm={handleDeleteConfirm}
                confirmText={t('delete')}
            />
            {/* 메시지 팝업 */}
            <Popup
                open={popup.open}
                message={popup.message}
                subMessage={popup.subMessage}
                onConfirm={popup.onConfirm}
            />
        </ContentArea>
    );
};

export default ContentsEdit;
